import { Container, Sprite, type DestroyOptions, type Texture } from "pixi.js";
import type { Log, PresentationTaskRunner } from "@slot/presentation";
import { POTS, type PotKind } from "../rules/symbols";
import { FIRE_STREAK_TUNING } from "../tuning/featureTuning";
import type { Point } from "./flightPath";
import { FX_SCOPE } from "./fxScope";
import { ART_SCALE, FIRE_STREAK_LAYOUT } from "./layout";

/**
 * The artist's fire streak (`fire_streak.riv`, played as its Spine conversion): a fireball flying right, its flames
 * trailing, from its start; its `land` trigger collapses it into a ring of fire on the spot (0.75 s), then it draws
 * nothing; `launch` relights it (0.17 s) to fly again. Its `color` a number per pot.
 */
export const FIRE_STREAK_RIVE = {
  launch: "launch",
  land: "land",
  color: "color",
  colors: { life: 1, multi: 2, grow: 3 } satisfies Record<PotKind, number>,
} as const;

/** What the streaks ask of their art (`SpineArtboard`: the Rive file's conversion, drawn into a texture of its own). */
export interface StreakArt {
  readonly texture: Texture;
  resize(scale: number): void;
  setNumber(name: string, value: number): void;
  trigger(name: string): void;
  advance(deltaSeconds: number): boolean;
  render(deltaSeconds: number): void;
  dispose(): void;
}

interface Streak {
  art: StreakArt;
  sprite: Sprite;
  /** Put back (hidden), falling (onto its target, or past the screen), or landing (its ring of fire playing out). */
  phase: "idle" | "falling" | "landing";
  /** Engine seconds of its landing still to draw. */
  landing: number;
  /** Whether it has flown: one flown before is relit to fly again. */
  flown: boolean;
  /** When it last set out, by count: when none is put back, the one landing longest is cut short. */
  order: number;
}

/** Design units per artboard pixel of a streak: the mock-up's scale, times the tuning's. */
const STREAK_SCALE = ART_SCALE * FIRE_STREAK_TUNING.scale;
/** How far a streak's front reaches ahead of its head, and its flames trail behind it (design units). */
const FRONT = (FIRE_STREAK_LAYOUT.artboard.width - FIRE_STREAK_LAYOUT.head.x) * STREAK_SCALE;
const TAIL = FIRE_STREAK_LAYOUT.head.x * STREAK_SCALE;
/** A streak's turn as it falls straight down (the artboard's fireball flies right). */
const DOWN = Math.PI / 2;

/**
 * The fire streaks an erupting pot sends to the board (the artist's handoff): fireballs in the pot's colour, each falling
 * straight down from over the screen's top onto its target, set out right above it (the user, 2026-10-12), at the
 * tuning's speed and size, and landing there in a ring of fire, which plays out as the game goes on. A MULTI streak
 * carries its multiplier's number on its head, caught on its cell's centre, where it pops, then settles on the cell's
 * corner; others of MULTI's carry nothing and fall on past the screen's bottom. A few per pot (the file loaded once), put
 * back once landed or past and relit to fly again; each drawn on the UI's runner while it falls and lands, at the
 * tuning's frame rate. The fall is the round's (its scope: a slam lands it at once); the ring plays out on the UI's
 * time, never work a round waits for. The layer is over everything: they fall over the board, the plaques and the pots,
 * a number over its fireball.
 */
export class FireStreaks extends Container {
  private readonly runner: PresentationTaskRunner;
  private readonly ambientRunner: PresentationTaskRunner;
  private readonly log: Log;
  /** A number to ride a streak's head ("×2"), upright and centred on its point; without it, none rides. */
  private readonly numberLabel: ((text: string) => Container) | null;
  private streaks: Readonly<Record<PotKind, readonly Streak[]>> | null = null;
  private readonly landed: Record<PotKind, number> = { life: 0, multi: 0, grow: 0 };
  private readonly passed: Record<PotKind, number> = { life: 0, multi: 0, grow: 0 };
  private drawing = false;
  private set = 0;
  /** Device pixels per design unit where the streaks fall (their canvases' size follows it). */
  private pixelsPerUnit = 1;

  public constructor(input: { runner: PresentationTaskRunner; ambientRunner: PresentationTaskRunner; log: Log; numberLabel?: (text: string) => Container }) {
    super({ label: "FireStreaks" });
    this.runner = input.runner;
    this.ambientRunner = input.ambientRunner;
    this.log = input.log;
    this.numberLabel = input.numberLabel ?? null;
  }

  /** The artist's streaks, loaded: each pot's few, in its colour. */
  public setArt(arts: Readonly<Record<PotKind, readonly StreakArt[]>>): void {
    if (this.destroyed || this.streaks) {
      for (const kind of POTS) for (const art of arts[kind]) art.dispose();
      return;
    }
    const { artboard, head } = FIRE_STREAK_LAYOUT;
    const build = (kind: PotKind, art: StreakArt): Streak => {
      art.setNumber(FIRE_STREAK_RIVE.color, FIRE_STREAK_RIVE.colors[kind]);
      art.resize(STREAK_SCALE * this.pixelsPerUnit);
      // Its head on its point, at its size, falling straight down.
      const sprite = new Sprite({ texture: art.texture, anchor: { x: head.x / artboard.width, y: head.y / artboard.height }, label: `Streak:${kind}` });
      sprite.setSize(artboard.width * STREAK_SCALE, artboard.height * STREAK_SCALE);
      sprite.rotation = DOWN;
      sprite.visible = false;
      this.addChild(sprite);
      return { art, sprite, phase: "idle", landing: 0, flown: false, order: 0 };
    };
    this.streaks = { life: arts.life.map((art) => build("life", art)), multi: arts.multi.map((art) => build("multi", art)), grow: arts.grow.map((art) => build("grow", art)) };
    this.log("[FireStreaks] the fire streaks are in");
  }

  /** Whether the streaks are in (until then a flight is the sprite it was). */
  public get ready(): boolean {
    return this.streaks !== null;
  }

  /** How many of each pot's streaks fall now, land now, have landed, and have passed the screen (the dev hook's read). */
  public get state(): { falling: Record<PotKind, number>; landing: Record<PotKind, number>; landed: Record<PotKind, number>; passed: Record<PotKind, number> } {
    const count = (kind: PotKind, phase: Streak["phase"]): number => this.streaks?.[kind].filter((streak) => streak.phase === phase).length ?? 0;
    const per = (phase: Streak["phase"]): Record<PotKind, number> => ({ life: count("life", phase), multi: count("multi", phase), grow: count("grow", phase) });
    return { falling: per("falling"), landing: per("landing"), landed: { ...this.landed }, passed: { ...this.passed } };
  }

  /** Device pixels per design unit where the streaks fall: their canvases are drawn at their size on the screen. */
  public setPixelsPerUnit(pixelsPerUnit: number): void {
    if (!(pixelsPerUnit > 0) || pixelsPerUnit === this.pixelsPerUnit) return;
    this.pixelsPerUnit = pixelsPerUnit;
    if (this.streaks) for (const kind of POTS) for (const streak of this.streaks[kind]) streak.art.resize(STREAK_SCALE * pixelsPerUnit);
  }

  /**
   * A streak of `pot`'s colour falls straight down onto `to`, set out right above it over the screen's top (`top`, this
   * layer's y): its head as far over it as its front reaches ahead, and the tuning's clearance, at its speed. It lands
   * as it is about to reach `to` — its front there, its head still the front's reach away (the user, 2026-10-12: the
   * impact starts as the fireball touches its target) — and resolves then, so what it lands on reacts with it; its head
   * flies on onto `to` as its ring of fire forms, and the ring plays out there. A `label` (a multiplier's "×n") rides its
   * head upright at `label.size` times its own size, is caught on `to` as the head gets there, pops there, then scales
   * down to its size on its way to `label.to`; the fall resolves once it is there. `onTouch` runs as it lands (an impact
   * of the caller's); with `ring` false it plays no ring of its own, its head flying on onto `to` under that impact, put
   * back there. A slam lands it at once, on `to`. It resolves false when nothing fell: before the streaks are in, or
   * while every one of the pot's falls.
   */
  public async fall(input: { pot: PotKind; to: Point; top: number; label?: { text: string; to: Point; size: number }; onTouch?: () => void; ring?: boolean }): Promise<boolean> {
    const streak = this.take(input.pot);
    if (!streak || this.destroyed) return false;
    const { speed, clearance } = FIRE_STREAK_TUNING;
    const { to } = input;
    const start = { x: to.x, y: Math.min(input.top, to.y) - FRONT - clearance };
    const length = to.y - start.y;
    // Its progress as its front reaches `to`.
    const touch = Math.max(0, 1 - FRONT / length);
    const order = this.setOut(streak, start);
    const { sprite, art } = streak;
    /** Whether the streak still flies this fall (not taken since for another). */
    const ours = (): boolean => !this.destroyed && !sprite.destroyed && streak.order === order;
    const label = input.label && this.numberLabel ? this.numberLabel(input.label.text) : null;
    if (label && input.label) {
      label.position.set(start.x, start.y);
      label.scale.set(input.label.size);
      this.addChild(label);
    }
    let touched!: () => void;
    const landing = new Promise<void>((resolve) => (touched = resolve));
    let landed = false;
    const ring = input.ring ?? true;
    const land = (): void => {
      if (landed) return;
      landed = true;
      touched();
      if (!ours()) return;
      this.landed[input.pot] += 1;
      input.onTouch?.();
      if (!ring) return;
      art.trigger(FIRE_STREAK_RIVE.land);
      streak.phase = "landing";
      streak.landing = FIRE_STREAK_TUNING.landSeconds;
      this.draw();
    };
    let arrived = false;
    let caught!: () => void;
    const arrival = new Promise<void>((resolve) => (caught = resolve));
    const arrive = (): void => {
      // On its target (a slam's end too): its ring of fire plays out there (or, ringless, it is put back under the
      // caller's impact), its number caught there.
      if (ours()) sprite.position.set(to.x, to.y);
      arrived = true;
      if (label && !label.destroyed) label.position.set(to.x, to.y);
      land();
      if (!ring && ours()) {
        sprite.visible = false;
        streak.phase = "idle";
      }
      caught();
    };
    void this.runner
      .tweenProgress(
        length / speed,
        (k) => {
          if (!ours()) return;
          sprite.position.set(to.x, start.y + length * k);
          // The number rides the head until the head gets to its target.
          if (label && !arrived && !label.destroyed) label.position.copyFrom(sprite.position);
          if (k >= touch) land();
        },
        FX_SCOPE,
        { easing: "linear" },
      )
      .then(arrive, arrive);
    await landing;
    if (label && input.label) {
      await arrival;
      await this.pop(label);
      await this.settle(label, input.label.to);
    }
    return true;
  }

  /**
   * A streak of `pot`'s colour falls straight through at `x`, from over the screen's top (`top`) on past its bottom
   * (`bottom`, both this layer's y), out of sight at both ends, at its speed: it carries nothing and lands nowhere
   * (MULTI's passers, the user 2026-10-12). Only a streak put back flies so, never one landing; with none free, none
   * passes. It resolves once past (a slam: at once).
   */
  public async pass(input: { pot: PotKind; x: number; top: number; bottom: number }): Promise<void> {
    const streak = this.streaks?.[input.pot].find((one) => one.phase === "idle");
    if (!streak || this.destroyed) return;
    const { speed, clearance } = FIRE_STREAK_TUNING;
    const start = { x: input.x, y: input.top - FRONT - clearance };
    const length = input.bottom + TAIL + clearance - start.y;
    const order = this.setOut(streak, start);
    const { sprite } = streak;
    const ours = (): boolean => !this.destroyed && !sprite.destroyed && streak.order === order;
    try {
      await this.runner.tweenProgress(
        length / speed,
        (k) => {
          if (ours()) sprite.position.set(start.x, start.y + length * k);
        },
        FX_SCOPE,
        { easing: "linear" },
      );
    } finally {
      if (ours()) {
        sprite.visible = false;
        streak.phase = "idle";
        this.passed[input.pot] += 1;
      }
    }
  }

  public override destroy(options?: DestroyOptions): void {
    const streaks = this.streaks;
    this.streaks = null;
    if (streaks) for (const kind of POTS) for (const streak of streaks[kind]) streak.art.dispose();
    super.destroy(options);
  }

  /** A streak set out on `start`, alight and drawn, falling: its fall's count (`order`). */
  private setOut(streak: Streak, start: Point): number {
    const { sprite, art } = streak;
    sprite.position.set(start.x, start.y);
    sprite.visible = true;
    streak.phase = "falling";
    streak.landing = 0;
    streak.order = ++this.set;
    // One flown before lights again; a new one sets out alight.
    if (streak.flown) art.trigger(FIRE_STREAK_RIVE.launch);
    streak.flown = true;
    art.render(0);
    this.draw();
    return streak.order;
  }

  /** A streak's number, caught, pops where it is: out to the tuning's peak and back. */
  private async pop(label: Container): Promise<void> {
    const size = label.scale.x;
    const { seconds, peak } = FIRE_STREAK_TUNING.labelPop;
    await this.runner.tweenProgress(
      seconds,
      (k) => {
        if (!label.destroyed) label.scale.set(size * (1 + (peak - 1) * Math.sin(Math.PI * k)));
      },
      FX_SCOPE,
      { easing: "linear" },
    );
    if (!label.destroyed) label.scale.set(size);
  }

  /**
   * A streak's number, caught and popped, scales down from the size it rode at to its own and moves to `to`, eased in
   * and out; then it goes (its cell shows it from there).
   */
  private async settle(label: Container, to: Point): Promise<void> {
    const from = { x: label.x, y: label.y };
    const size = label.scale.x;
    try {
      await this.runner.tweenProgress(
        FIRE_STREAK_TUNING.labelSeconds,
        (k) => {
          if (label.destroyed) return;
          const along = 0.5 - 0.5 * Math.cos(Math.PI * k);
          label.position.set(from.x + (to.x - from.x) * along, from.y + (to.y - from.y) * along);
          label.scale.set(size + (1 - size) * along);
        },
        FX_SCOPE,
        { easing: "linear" },
      );
    } finally {
      if (!label.destroyed) label.destroy({ children: true });
    }
  }

  /** A put-back streak of `pot`'s colour; else the one landing longest, its ring cut short; none while all fall. */
  private take(pot: PotKind): Streak | null {
    const streaks = this.streaks?.[pot] ?? [];
    const idle = streaks.find((streak) => streak.phase === "idle");
    if (idle) return idle;
    return streaks.filter((streak) => streak.phase === "landing").sort((a, b) => a.order - b.order)[0] ?? null;
  }

  /** Whether any streak falls or lands. */
  private get busy(): boolean {
    return POTS.some((kind) => this.streaks?.[kind].some((streak) => streak.phase !== "idle") ?? false);
  }

  /** The streaks in the air or landing drawn at their own pace; each put back once its ring has played out. */
  private draw(): void {
    if (this.drawing) return;
    this.drawing = true;
    let last = 0;
    void this.ambientRunner
      .tickInScope(
        "",
        (elapsed) => {
          const delta = elapsed - last;
          last = elapsed;
          for (const kind of POTS) {
            for (const streak of this.streaks?.[kind] ?? []) {
              if (streak.phase === "idle") continue;
              streak.art.advance(delta);
              if (streak.phase !== "landing") continue;
              streak.landing = Math.max(0, streak.landing - delta);
              if (streak.landing > 0) continue;
              streak.phase = "idle";
              streak.sprite.visible = false;
            }
          }
        },
        () => this.destroyed || !this.busy,
      )
      .then(() => {
        this.drawing = false;
        // One set out as the last was put back.
        if (!this.destroyed && this.busy) this.draw();
      });
  }
}
